import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import countryList from 'react-select-country-list';
import moment from 'moment';

import { Container, Wrapper } from 'components/Layout/Layout';
import { RectangleButton } from 'components/Button/RectangleButton';
import { useActions } from 'hooks/useActions';
import * as actions from './actions';
import Path from 'routes/Path';
import { redirectToCheckout } from 'functions/redirectToCheckout';
import { checkVATForCountries } from 'functions/checkVATForCountries';
import { Spinner } from 'components/Spinner/Spinner';
import Dropdown from 'components/CustomizableDropdown/CustomizableDropdown';
import OptionsTab from './OptionsTab';
import PurchaseSelection from 'components/PurchaseSelection/PurchaseSelection';
import YesNoModal from 'components/Modals/yesNoModal';
import euFlag from 'assets/Images/eu.svg';
import svFlag from 'assets/flags/sv.svg';
import {
  CurrencyDropdownButton,
  DropdownItem,
  DropdownMenuBlock,
  CurrencyFlag,
  Offer,
  OffersContainer,
  PlanContainer,
  PlanType,
  PurchaseTab,
} from 'components/PurchaseSelection/styles';
import {
  PlansContainer,
  PlansWrapper,
  TabsContainer,
  LabelHeading,
  EmailLink,
  PaymentPageLink,
  VATInput,
  VATStatusText,
  SubscriptionAmount,
  SubscriptionContainer,
  CurrencyIconAmountAndMonthContainer,
  CurrencyIconAndAmountContainer,
  CurrentIcon,
  PerMonthText,
  CancelSubscriptionContainer,
} from './styles';

const countriesList = countryList().getData();

export default function PlansPage() {
  const [countries, setCountries] = useState([]);
  const [hasCountry, setHasCountry] = useState(false);
  const [hasVatId, setHasVatId] = useState(false);
  const [vatId, setVatId] = useState('');
  const [vatStatus, setVatStatus] = useState('');
  const [country, setCountry] = useState('');
  const [isPurchaseTab, setIsPurchaseTab] = useState(true);
  const [planDetails, setPlanDetails] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState('SEK');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { loading, plan, payment, updatedCompany, isSubscriptionCancelled, error } = useSelector(
    (state) => state.PlansPageReducer
  );
  const { company } = useSelector((state) => state.auth);

  const { getPlan, updateCompany, createPayment, cancelSubscription } = useActions(actions);

  const currencies = useMemo(() => (plan._id ? Object.keys(plan.price) : []), [plan]);

  useEffect(() => {
    getPlan();
    setCountries([{ label: 'Select country', value: '' }, ...countriesList]);
  }, [getPlan]);

  useEffect(() => {
    if (plan._id) setSelectedCurrency(Object.keys(plan.price)?.[0]);
  }, [plan]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (isSubscriptionCancelled) {
      toast.success('Subscription cancelled successfully');
      getPlan();
    }
  }, [isSubscriptionCancelled, getPlan]);

  useEffect(() => {
    if (payment?.id) redirectToCheckout(payment);
  }, [payment]);

  useEffect(() => {
    if (updatedCompany?._id) {
      setHasCountry(true);
      setCountry(updatedCompany.country);
      setHasVatId(true);
      setVatId(updatedCompany.VATInformation.vatId);
      setVatStatus(updatedCompany.VATInformation.verification.status);
    }
  }, [updatedCompany]);

  useEffect(() => {
    if (company?._id) {
      if (company.country) {
        setCountry(company.country);
        setHasCountry(true);
      }
      if (company?.VATInformation?.vatId) {
        setHasVatId(true);
        setVatId(company.VATInformation.vatId);
        setVatStatus(company.VATInformation.verification.status);
      }
    }
  }, [company]);

  const purchaseTest = () => {
    let params = {};
    if (!hasCountry && country) params.country = country;
    if (!hasVatId && vatId) {
      params.VATInformation = {
        vatId: vatId,
      };
      updateCompany(company._id, params);
    } else if (plan.recurring) {
      params.planId = plan._id;
      params.planType = plan.name;
      params.currency = selectedCurrency;
      createPayment(params);
    } else {
      params.planId = planDetails._id;
      params.currency = planDetails.selectedCurrency;
      params.noOfTests = planDetails.credits;
      params.planType = planDetails.name;
      createPayment(params);
    }
  };

  const isPurchaseButtonDisabled = () => {
    if (!hasCountry && !country) return true;
    if (hasVatId && vatStatus === 'pending') return true;
    return false;
  };

  const renderCountrySelection = () => {
    return (
      <>
        {(!hasCountry || (hasCountry && !hasVatId && checkVATForCountries(country))) && (
          <Dropdown
            width="200px"
            value={country}
            margin="9px 0 0 0"
            justifyContent="center"
            options={countries}
            onChange={(result) => setCountry(result.value)}
          />
        )}
        {country && !hasVatId && checkVATForCountries(country) && (
          <VATInput
            type="text"
            placeholder="VAT ID"
            name="VAT"
            value={vatId}
            onChange={(e) => setVatId(e.target.value)}
          />
        )}
      </>
    );
  };

  const renderVATStatus = () => {
    if (vatStatus === 'pending')
      return (
        <VATStatusText>
          VAT verification is pending. This usually takes 1-2 minutes. <br />
          You can see the status on your company settings page.
          <br />
          Please wait until the verification is completed otherwise you will get charged 25% VAT.
        </VATStatusText>
      );
  };

  const renderPurchaseTab = () => (
    <TabsContainer>
      <PurchaseSelection purchaseData={plan} setPurchaseDetails={setPlanDetails} />
      {renderCountrySelection()}
      <span>
        <RectangleButton disabled={isPurchaseButtonDisabled()} onClick={() => purchaseTest()}>
          Purchase
        </RectangleButton>
      </span>
      {renderVATStatus()}
    </TabsContainer>
  );

  const renderCancelSubscription = () => {
    return (
      <CancelSubscriptionContainer>
        <p>
          Your current plan remains active until {moment(plan.expireAt).format('DD-MM-YYYY')}. To cancel your
          subscription, simply click on the "Cancel Subscription" button.
        </p>
        <RectangleButton floatRight onClick={() => setShowConfirmationModal(true)}>
          Cancel Subscription
        </RectangleButton>
      </CancelSubscriptionContainer>
    );
  };

  const renderPlanCancelledText = () => {
    return (
      <CancelSubscriptionContainer>
        You've opted to discontinue your freecode+ plan, yet you can still access freecode+ test features until{' '}
        {moment(plan.expireAt).format('DD-MM-YYYY')}.
      </CancelSubscriptionContainer>
    );
  };
  console.log('plan: ', plan);

  const renderFreecodePlusSubscriptionTab = () => (
    <TabsContainer>
      <PurchaseTab>
        <OffersContainer>
          <Offer>Pay on monthly basis to enable the freecode+ tests</Offer>
        </OffersContainer>
        <PlanContainer>
          <PlanType>Freecode Plus</PlanType>
          <SubscriptionContainer>
            <CurrencyIconAmountAndMonthContainer>
              <CurrencyIconAndAmountContainer>
                <CurrentIcon>{selectedCurrency === 'EUR' && <i className="fa fa-eur" />}</CurrentIcon>
                <SubscriptionAmount>{plan.price[selectedCurrency]}&nbsp;</SubscriptionAmount>
              </CurrencyIconAndAmountContainer>
              <PerMonthText>per month</PerMonthText>
            </CurrencyIconAmountAndMonthContainer>
            <CurrencyDropdownButton
              type="button"
              id="dropdownMenuButton"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              {selectedCurrency}
            </CurrencyDropdownButton>
            <DropdownMenuBlock aria-labelledby="dropdownMenuButton">
              {currencies.map((currency, key) => {
                return (
                  <DropdownItem key={key} value={currency} onClick={() => setSelectedCurrency(currency)}>
                    {currency}
                    <CurrencyFlag src={currency === 'SEK' ? svFlag : euFlag} />
                  </DropdownItem>
                );
              })}
            </DropdownMenuBlock>
          </SubscriptionContainer>
        </PlanContainer>
        {renderCountrySelection()}
      </PurchaseTab>
      <span>
        <RectangleButton
          disabled={isPurchaseButtonDisabled()}
          marginTop
          isOldButton
          width="202px"
          onClick={() => purchaseTest()}
        >
          Buy
        </RectangleButton>
      </span>
      {renderVATStatus()}
    </TabsContainer>
  );

  const renderRecurringTab = () => (
    <TabsContainer>
      <LabelHeading>To purchase a plan, please contact</LabelHeading>
      <EmailLink href="mailto:sales@futureskill.com">sales@futureskill.com</EmailLink>
    </TabsContainer>
  );

  const showFreecodePlusSubscription = useMemo(() => company?.limitless, [company]);

  return (
    <Wrapper minHeight="100vh">
      {showConfirmationModal && (
        <YesNoModal
          modalInfo={{ title: 'Cancel Subscription', body: 'Are you sure you want to cancel this subscription?' }}
          confirm={(isConfirmed) => {
            if (isConfirmed) cancelSubscription();
            setShowConfirmationModal(false);
          }}
        />
      )}
      <Container usePadding="0" column full>
        <PlansContainer>
          <PlansWrapper>
            {loading ? (
              <Spinner
                title="Please wait while your payment is processing"
                text="Do not refresh / reload the page until processing is done..."
              />
            ) : (
              <>
                {plan.hasActiveSubscription
                  ? plan.cancelled
                    ? renderPlanCancelledText()
                    : renderCancelSubscription()
                  : plan._id && (
                      <>
                        <OptionsTab
                          isFreecodePlusSubscription={showFreecodePlusSubscription}
                          handleTabs={(tab) => setIsPurchaseTab(tab === 'purchase')}
                          activeTab={
                            showFreecodePlusSubscription ? 'recurring' : isPurchaseTab ? 'purchase' : 'recurring'
                          }
                          options={showFreecodePlusSubscription ? ['recurring'] : ['recurring', 'purchase']}
                        />
                        {showFreecodePlusSubscription
                          ? renderFreecodePlusSubscriptionTab()
                          : isPurchaseTab
                            ? renderPurchaseTab()
                            : renderRecurringTab()}
                        {!showFreecodePlusSubscription && (
                          <PaymentPageLink href={Path.payments}>
                            Click here to see your previous purchases
                          </PaymentPageLink>
                        )}
                      </>
                    )}
              </>
            )}
          </PlansWrapper>
        </PlansContainer>
      </Container>
    </Wrapper>
  );
}
